import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useToast } from '../../contexts/ToastContext';
import { useCms } from '../../contexts/CmsContext';
import { Mail, Phone, MapPin, Clock, Send, MessageCircle, ChevronRight } from 'lucide-react';
import { trackWhatsAppClick } from '../../services/tracking';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const iconMap: Record<string, React.ElementType> = { Mail, Phone, MapPin, Clock };

export default function ContactScreen(): React.JSX.Element {
  const cms = useCms();
  const toast = useToast();

  const contactInfo = [
    { icon: 'Mail', label: 'Email', value: cms.get('contact_email', 'hello@ecoleminds.com'), href: `mailto:${cms.get('contact_email', 'hello@ecoleminds.com')}` },
    { icon: 'Phone', label: 'Phone', value: cms.get('contact_phone', '+91 98765 43210'), href: `tel:${cms.get('contact_phone', '+919876543210').replace(/\s/g, '')}` },
    { icon: 'MapPin', label: 'Address', value: cms.get('contact_address', 'WeWork, Embassy Tech Village, Bangalore, India'), href: '#' },
    { icon: 'Clock', label: 'Hours', value: cms.get('contact_hours', 'Mon–Sat: 9:00 AM – 7:00 PM IST'), href: '#' },
  ];
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.show('Please fill in all required fields', 'error');
      return;
    }
    setLoading(true);
    // Simulate API call
    await new Promise(r => setTimeout(r, 1500));
    toast.show('Message sent successfully! We will get back to you soon.', 'success');
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-50 text-amber-500 mb-6"
          >
            Contact Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-4"
          >
            Get in Touch
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-600 max-w-xl mx-auto"
          >
            Have questions about our courses? Want to partner with us? We'd love to hear from you.
          </motion.p>
        </div>
      </section>

      {/* Contact Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              {contactInfo.map((item, i) => {
                const Icon = iconMap[item.icon] || Mail;
                return (
                  <motion.a
                    key={i}
                    href={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-start gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-100 transition-colors">
                      <Icon className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{item.label}</p>
                      <p className="text-sm font-semibold text-slate-900 mt-0.5">{item.value}</p>
                    </div>
                  </motion.a>
                );
              })}

              {/* WhatsApp CTA */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="p-6 bg-green-50 rounded-2xl border border-green-100 mt-8"
              >
                <div className="flex items-center gap-3 mb-3">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                  <h3 className="font-bold text-slate-900">Prefer WhatsApp?</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Chat with us directly for quick responses about courses, payments, and enrollment.
                </p>
                <a
                  href="https://wa.me/919876543210?text=Hi%20EcoleMinds,%20I%20have%20a%20question%20about%20your%20courses&ref=wa_contact_page"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('wa_contact_page')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 transition-colors"
                >
                  Chat on WhatsApp <ChevronRight className="w-4 h-4" />
                </a>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Input
                      label="Full Name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Input
                      label="Phone (Optional)"
                      type="tel"
                      value={form.phone}
                      onChange={e => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                    <Input
                      label="Subject"
                      value={form.subject}
                      onChange={e => setForm({ ...form, subject: e.target.value })}
                      placeholder="Course Inquiry"
                    />
                  </div>
                  <Input
                    label="Message"
                    multiline
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us how we can help you..."
    
                    required
                  />
                  <Button type="submit" isLoading={loading} className="w-full" leftIcon={<Send className="w-4 h-4" />}>
                    Send Message
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map placeholder */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="bg-slate-200 rounded-2xl h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-10 h-10 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-500 font-medium">WeWork, Embassy Tech Village</p>
              <p className="text-sm text-slate-400">Bangalore, Karnataka, India</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
